import axios from 'axios'
import api from './api'
import type {
  Aspect,
  FormatRender,
  TourCreateRequest,
  BatchRoomUploadUrlRequest,
  BatchUploadUrlResponse,
  ConfirmUploadRequest,
  TourResponse,
  TourRoomResponse,
  TourStatus,
  RoomStatus,
  TourListItem,
  RoomFailureKind,
} from '@/types/tour'

export interface TourStatusRoom {
  id: number
  room_order: number
  status: RoomStatus
  room_type: string | null
  video_s3_key: string | null
  current_step: string | null
  error_message: string | null
  failure_kind?: RoomFailureKind | null
}

export interface TourStatusResponse {
  id: number
  status: TourStatus
  rooms: TourStatusRoom[]
  /** Each format's render (`branding_stale` not compared here); absent from an older API. */
  renders?: Partial<Record<Aspect, FormatRender>>
  media_urls_expire_at?: string | null
}

export async function createTour(data: TourCreateRequest): Promise<TourResponse> {
  const res = await api.post<TourResponse>('/tours/', data)
  return res.data
}

export async function batchGetUploadUrls(
  tourId: number,
  rooms: BatchRoomUploadUrlRequest['rooms'],
): Promise<BatchUploadUrlResponse> {
  const res = await api.post<BatchUploadUrlResponse>(
    `/tours/${tourId}/rooms/batch-upload-urls`,
    { rooms },
  )
  return res.data
}

// A PUT that sends nothing for this long has stalled: it lands in Upload failed, with Retry. A slow but steady one
// (three 20 MB photos at once on a slow line take minutes) goes on as long as it keeps moving.
export const S3_STALL_MS = 30_000
// Backstop for a PUT that crawls forever: 20 MB at 0.2 Mbit/s is ~13 min.
export const S3_PUT_TIMEOUT_MS = 20 * 60_000

export async function uploadFileToS3(
  presignedUrl: string,
  file: Blob,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  // Its own controller: the caller's signal (a delete) aborts it, and so does a stall.
  const ctrl = new AbortController()
  const abort = () => ctrl.abort()
  if (signal?.aborted) abort()
  signal?.addEventListener('abort', abort)
  let stall = setTimeout(abort, S3_STALL_MS)
  try {
    await axios.put(presignedUrl, file, {
      headers: { 'Content-Type': file.type },
      timeout: S3_PUT_TIMEOUT_MS,
      transitional: { clarifyTimeoutError: true },
      signal: ctrl.signal,
      onUploadProgress: (e) => {
        clearTimeout(stall)
        stall = setTimeout(abort, S3_STALL_MS)
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100))
      },
    })
  } finally {
    clearTimeout(stall)
    signal?.removeEventListener('abort', abort)
  }
}

export async function confirmUpload(
  tourId: number,
  roomId: number,
  s3Key: string,
  displayKeys?: string[],
): Promise<void> {
  await api.post(`/tours/${tourId}/rooms/${roomId}/confirm-upload`, {
    s3_key: s3Key,
    ...(displayKeys?.length ? { display_keys: displayKeys } : {}),
  } satisfies ConfirmUploadRequest)
}

/**
 * Analyse the staged rooms that need it; `roomIds` narrows the set further.
 * `useAiMoves` drops their chosen camera moves first so the analysis picks.
 */
export async function processTour(
  tourId: number,
  roomIds?: number[],
  { useAiMoves = false }: { useAiMoves?: boolean } = {},
): Promise<void> {
  const body = {
    ...(roomIds && roomIds.length > 0 ? { room_ids: roomIds } : {}),
    ...(useAiMoves ? { use_ai_moves: true } : {}),
  }
  await api.post(`/tours/${tourId}/process`, Object.keys(body).length ? body : undefined)
}

/** `regenerate`: the listed rooms may already have a clip; a new one replaces it. */
export async function generateTour(
  tourId: number,
  roomIds?: number[],
  { regenerate = false } = {},
): Promise<void> {
  const body = roomIds && roomIds.length > 0
    ? { room_ids: roomIds, ...(regenerate ? { regenerate: true } : {}) }
    : undefined
  await api.post(`/tours/${tourId}/generate`, body)
}

export async function getTour(tourId: number, signal?: AbortSignal): Promise<TourResponse> {
  const res = await api.get<TourResponse>(`/tours/${tourId}`, { signal })
  return res.data
}

export async function getTourStatus(tourId: number): Promise<TourStatusResponse> {
  const res = await api.get<TourStatusResponse>(`/tours/${tourId}/status`)
  return res.data
}

export interface PresignedUrlResponse {
  url: string
  expires_at: string
  s3_key: string
  /** A clip's real length (`video-url` only; absent from an API before it, null when unknown). */
  duration_seconds?: number | null
}

export type TourSort = 'updated_at' | 'created_at' | 'title'

/** Tours per `GET /tours/` page; the server allows up to 500. */
export const LIST_PAGE = 200

/** Every tour of the user's: the Library renders and searches all of them, so this pages until a short page. */
export async function listTours(): Promise<TourListItem[]> {
  const tours: TourListItem[] = []
  const seen = new Set<number>()
  for (let skip = 0; ; skip += LIST_PAGE) {
    const res = await api.get<TourListItem[]>('/tours/', { params: { skip, limit: LIST_PAGE } })
    // A tour edited mid-way moves up the updated_at order and can come back on a later page.
    for (const tour of res.data) {
      if (!seen.has(tour.id)) {
        seen.add(tour.id)
        tours.push(tour)
      }
    }
    if (res.data.length < LIST_PAGE) return tours
  }
}

/** Move a tour between folders. `null` puts it back in the ungrouped row. */
export async function updateTour(
  tourId: number,
  data: { title?: string; folder_id?: number | null; aspect_ratio?: Aspect },
): Promise<TourResponse> {
  const res = await api.patch<TourResponse>(`/tours/${tourId}`, data)
  return res.data
}

export async function deleteTour(tourId: number): Promise<void> {
  await api.delete(`/tours/${tourId}`)
}

export async function deleteRoom(tourId: number, roomId: number): Promise<void> {
  await api.delete(`/tours/${tourId}/rooms/${roomId}`)
}

/** A moved room as the server left it: a failed one comes back reset, ready for Generate clips (GET-113). */
export interface RoomStageState {
  id: number
  status: string
  failure_kind?: RoomFailureKind | null
}

export interface RoomStageResponse {
  updated: number[]
  /** Absent from an older API. */
  rooms?: RoomStageState[]
}

/** Move rooms between the video's working set and its photo storage. */
export async function stageRooms(
  tourId: number,
  roomIds: number[],
  isStaged: boolean,
): Promise<RoomStageResponse> {
  const res = await api.patch<RoomStageResponse>(`/tours/${tourId}/rooms/stage`, {
    room_ids: roomIds,
    is_staged: isStaged,
  })
  return res.data
}

/** Save the row order (= clip order) as room_order. Unlisted rooms follow. */
export async function reorderRooms(tourId: number, roomIds: number[]): Promise<void> {
  await api.patch(`/tours/${tourId}/rooms/order`, { room_ids: roomIds })
}

/** Copy a room's photo (and analysis, if any) onto a new room. Free. */
/** How a room's current clip plays in the video, and its 9:16 window. Free; never a new clip. */
export async function setRoomPlayback(
  tourId: number,
  roomId: number,
  playback: { speed?: number; reversed?: boolean; crop_x?: number },
): Promise<{ playback_speed: number; playback_reversed: boolean; crop_x: number | null }> {
  const res = await api.patch(`/tours/${tourId}/rooms/${roomId}/playback`, playback)
  return res.data
}

/** A new room on the same photo. `withClip` shares the source's clip too. */
export async function duplicateRoom(
  tourId: number,
  roomId: number,
  withClip = false,
): Promise<TourRoomResponse> {
  const res = await api.post<TourRoomResponse>(
    `/tours/${tourId}/rooms/${roomId}/duplicate`,
    withClip ? { with_clip: true } : undefined,
  )
  return res.data
}

export async function getRoomVideoUrl(
  tourId: number,
  roomId: number,
): Promise<PresignedUrlResponse> {
  const res = await api.get<PresignedUrlResponse>(`/tours/${tourId}/rooms/${roomId}/video-url`)
  return res.data
}

export async function updateRoomPrompt(
  tourId: number,
  roomId: number,
  overrides: { motion?: string; reverse?: boolean },
): Promise<void> {
  await api.patch(`/tours/${tourId}/rooms/${roomId}/prompt`, overrides)
}

export async function generateComposite(tourId: number, clipOrder: number[], aspect: Aspect): Promise<void> {
  await api.post(`/tours/${tourId}/composite`, { clip_order: clipOrder, aspect })
}

export async function getCompositeDownloadUrl(tourId: number, aspect: Aspect): Promise<{ url: string }> {
  const res = await api.get<{ url: string }>(`/tours/${tourId}/composite-download`, { params: { aspect } })
  return res.data
}

// ── Listing import (Rightmove) ───────────────────────────────────────────────

export interface ListingImportResponse {
  job_id: number
  status: string
}

export type ListingImportStatus =
  | 'pending'
  | 'fetching'
  | 'awaiting_selection'
  | 'completed'
  | 'failed'

export interface ListingImportStatusResponse {
  job_id: number
  status: ListingImportStatus
  candidate_photos: string[] | null
  error: string | null
  created_at: string
  updated_at: string
}

export async function importListing(
  tourId: number,
  url: string,
): Promise<ListingImportResponse> {
  const res = await api.post<ListingImportResponse>(
    `/tours/${tourId}/import-listing`,
    { url },
  )
  return res.data
}

export async function getListingImportStatus(
  tourId: number,
  jobId: number,
): Promise<ListingImportStatusResponse> {
  const res = await api.get<ListingImportStatusResponse>(
    `/tours/${tourId}/import-listing/${jobId}`,
  )
  return res.data
}

// ── Batch URL import (listing photos downloaded server-side) ─────────────────

export async function batchImportUrls(
  tourId: number,
  rooms: { url: string; filename: string; room_order?: number; is_staged?: boolean }[],
): Promise<{ rooms: { room_id: number; s3_key: string }[] }> {
  const res = await api.post<{ rooms: { room_id: number; s3_key: string }[] }>(
    `/tours/${tourId}/rooms/batch-import-urls`,
    { rooms },
  )
  return res.data
}


